<template>
  <div class="stl-panel">
    <b-overlay :show="loading" rounded>
      <div v-if="!hasDraft" class="text-center text-muted py-4">
        Phiếu chưa bắt đầu quyết toán.
      </div>

      <template v-else>
        <!-- ============ Preview 2 hoá đơn (Tiền hàng / VAT / Tổng) ============ -->
        <b-row class="mb-3">
          <b-col md="6">
            <div class="stl-total-card stl-total-customer">
              <div class="stl-total-label">
                <i class="fas fa-user mr-1"></i> Hoá đơn Khách trả
              </div>
              <div class="stl-total-line">
                <span>Tiền hàng</span><span>{{ formatPrice(preview.customer.goods) }}</span>
              </div>
              <div class="stl-total-line">
                <span>VAT</span><span>{{ formatPrice(preview.customer.vat) }}</span>
              </div>
              <div class="stl-total-line stl-total-sum">
                <span>Tổng</span><span>{{ formatPrice(preview.customer.total) }}</span>
              </div>
              <div class="stl-total-tag">Xem trước hoá đơn</div>
            </div>
          </b-col>
          <b-col md="6">
            <div class="stl-total-card stl-total-insurance">
              <div class="stl-total-label">
                <i class="fas fa-shield-alt mr-1"></i> Hoá đơn Bảo hiểm trả
              </div>
              <div class="stl-total-line">
                <span>Tiền hàng</span><span>{{ formatPrice(preview.insurance.goods) }}</span>
              </div>
              <div class="stl-total-line">
                <span>VAT</span><span>{{ formatPrice(preview.insurance.vat) }}</span>
              </div>
              <div class="stl-total-line stl-total-sum">
                <span>Tổng</span><span>{{ formatPrice(preview.insurance.total) }}</span>
              </div>
              <div class="stl-total-tag">Xem trước hoá đơn</div>
            </div>
          </b-col>
        </b-row>

        <!-- ============ Cảnh báo lệch SL báo giá / thực dùng ============ -->
        <b-alert
          v-if="quantityMismatch.length"
          show
          variant="warning"
          class="stl-mismatch d-flex align-items-start"
        >
          <i class="fas fa-balance-scale-right mr-2 mt-1"></i>
          <div>
            <b>Số lượng vật tư trên báo giá khác với số thực dùng</b>
            <ul class="stl-mismatch-list mb-1">
              <li v-for="m in quantityMismatch" :key="m.productId">
                {{ m.name }}: báo giá <b>{{ m.quotedQty }}</b> — thực dùng
                <b>{{ m.actualUsedQty }}</b>
              </li>
            </ul>
            <div class="stl-mismatch-note">
              Hoá đơn tính theo <b>BÁO GIÁ</b>; phiếu xuất kho theo <b>THỰC DÙNG</b>. Nếu chênh lệch
              là do phát sinh, hãy cập nhật lại báo giá trước khi duyệt quyết toán.
            </div>
          </div>
        </b-alert>

        <!-- ============ Bảng dòng quyết toán (nguồn: BÁO GIÁ) ============ -->
        <div class="table-responsive">
          <table class="table table-bordered stl-table">
            <thead>
              <tr>
                <th style="width: 40px">#</th>
                <th style="width: 90px">Nguồn</th>
                <th style="min-width: 220px">Nội dung</th>
                <th class="text-center" style="width: 70px">SL</th>
                <th class="text-right" style="width: 130px">Đơn giá</th>
                <th class="text-right" style="width: 140px">Thành tiền</th>
                <th class="text-center" style="width: 80px">VAT</th>
                <th class="text-center" style="width: 210px">Người trả</th>
                <th class="text-center" style="width: 110px">Tách dòng</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in rows">
                <tr
                  :key="row.line.id"
                  :class="{
                    'stl-row-parent': row.type === 'parent',
                    'stl-row-child': row.type === 'child',
                  }"
                >
                  <td class="text-center align-middle">
                    <span v-if="row.type !== 'child'">{{ row.seq }}</span>
                    <i v-else class="fas fa-level-up-alt fa-rotate-90 text-muted"></i>
                  </td>
                  <td class="align-middle">
                    <b-badge :variant="row.line.source === 'MATERIAL' ? 'info' : 'primary'">
                      {{ sourceLabel(row.line.source) }}
                    </b-badge>
                  </td>
                  <td class="align-middle">
                    {{ row.line.desc || '—' }}
                    <span v-if="row.type === 'parent'" class="text-muted small d-block">
                      (đã tách — không tính vào tổng)
                    </span>
                  </td>
                  <td class="text-center align-middle">{{ row.line.qty || '—' }}</td>
                  <td class="text-right align-middle">
                    {{ row.line.unitPrice ? formatPrice(row.line.unitPrice) : '—' }}
                  </td>
                  <td class="text-right align-middle">
                    <template v-if="row.type === 'child' && editable">
                      <b-form-input
                        v-model.number="row.line.amount"
                        type="number"
                        min="0"
                        size="sm"
                        class="text-right"
                        @input="onChildAmount(row.line)"
                      />
                    </template>
                    <span
                      v-else
                      :class="{ 'text-muted': row.type === 'parent' }"
                      >{{ formatPrice(row.line.amount) }}</span
                    >
                  </td>
                  <td class="text-center align-middle">
                    {{ vatLabel(row.line.vatRate) }}
                  </td>
                  <td class="text-center align-middle">
                    <!-- Parent: không chọn người trả (đã tách theo dòng con) -->
                    <span v-if="row.type === 'parent'" class="text-muted">—</span>
                    <!-- Child: người trả cố định theo dòng con -->
                    <b-badge
                      v-else-if="row.type === 'child'"
                      :variant="row.line.billTarget === 'INSURANCE' ? 'warning' : 'success'"
                    >
                      {{ targetLabel(row.line.billTarget) }}
                    </b-badge>
                    <!-- Normal: toggle Khách / BH -->
                    <b-form-radio-group
                      v-else
                      :checked="row.line.billTarget"
                      :disabled="!editable"
                      size="sm"
                      buttons
                      button-variant="outline-secondary"
                      @input="(val) => setTarget(row.line, val)"
                    >
                      <b-form-radio value="CUSTOMER">Khách</b-form-radio>
                      <b-form-radio value="INSURANCE">BH</b-form-radio>
                    </b-form-radio-group>
                  </td>
                  <td class="text-center align-middle">
                    <b-button
                      v-if="row.type === 'normal' && editable"
                      size="sm"
                      variant="link"
                      class="p-0"
                      title="Tách dòng: 1 phần Khách trả + 1 phần BH trả"
                      @click="splitLine(row.line)"
                    >
                      <i class="fas fa-columns"></i> Tách
                    </b-button>
                    <b-button
                      v-else-if="row.type === 'parent' && editable"
                      size="sm"
                      variant="link"
                      class="p-0 text-danger"
                      title="Gộp lại (bỏ tách)"
                      @click="unsplit(row.line)"
                    >
                      <i class="fas fa-compress-alt"></i> Gộp
                    </b-button>
                  </td>
                </tr>
              </template>
              <tr v-if="!lines.length">
                <td colspan="9" class="text-center text-muted py-3">
                  Không có dòng quyết toán.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="splitError" class="text-danger small mb-2">
          <i class="fas fa-exclamation-triangle"></i> {{ splitError }}
        </div>

        <!-- ============ Ghi chú (chỉ khi đang phân bổ) ============ -->
        <p v-if="editable" class="stl-hint text-muted small">
          <i class="fas fa-info-circle mr-1"></i>
          Các dòng trên lấy <b>từ báo giá</b> (đã trừ chiết khấu). Tick <b>Khách / BH</b> cho từng
          dòng, hoặc <b>Tách</b> khi một dòng do cả hai cùng chi trả (tổng 2 phần = dòng gốc). Nhớ
          <b>Lưu phân bổ</b> trước khi Duyệt quyết toán.
        </p>

        <!-- ============ Thanh hành động (footer) ============ -->
        <div class="stl-actions">
          <!-- Trạng thái quyết toán -->
          <div class="stl-actions-state">
            <b-badge v-if="isReleased" variant="dark" class="stl-state-pill">
              <i class="fas fa-flag-checkered mr-1"></i> Đã xuất xưởng
            </b-badge>
            <b-badge v-else-if="isSettled" variant="success" class="stl-state-pill">
              <i class="fas fa-check-circle mr-1"></i> Đã quyết toán
            </b-badge>
            <b-badge v-else variant="light" class="stl-state-pill stl-state-pill--draft">
              <i class="fas fa-pen-nib mr-1"></i> Đang phân bổ
            </b-badge>
          </div>

          <!-- Nút thao tác -->
          <div class="stl-actions-btns">
            <b-button
              v-if="editable"
              variant="primary"
              :disabled="saving || !!splitError"
              @click="save"
            >
              <b-spinner v-if="saving" small /> <i v-else class="fas fa-save mr-1"></i>Lưu phân bổ
            </b-button>
            <b-button
              v-if="(editable || isSettled) && canSettle"
              variant="outline-secondary"
              :disabled="undoing || hasBills"
              :title="hasBills ? 'Đã xuất hoá đơn — không thể hoàn tác' : ''"
              @click="undo"
            >
              <b-spinner v-if="undoing" small /> <i v-else class="fas fa-undo mr-1"></i>Hoàn tác
            </b-button>
          </div>
        </div>

        <!-- ============ Xuất hoá đơn (mặt TIỀN) — khi đã quyết toán; RELEASED chỉ XEM ============ -->
        <div v-if="isSettled || isReleased" class="stl-invoice">
          <div class="stl-invoice-head">
            <i class="fas fa-file-invoice-dollar mr-1"></i>
            <b>Hoá đơn</b>
            <span class="stl-invoice-note">
              {{ isReleased ? 'đã xuất xưởng — chỉ xem' : 'mỗi người trả 1 hoá đơn — đã xuất thì không tạo lại' }}
            </span>
          </div>
          <div class="stl-invoice-body">
            <!-- Khách trả -->
            <div class="stl-invoice-row">
              <div class="stl-invoice-payer">
                <i class="fas fa-user stl-ic-customer"></i> Khách trả
                <span class="stl-invoice-amount">{{ formatPrice(preview.customer.total) }}</span>
              </div>
              <div class="stl-invoice-act">
                <a
                  v-if="customerBill"
                  :href="billLink(customerBill)"
                  target="_blank"
                  class="stl-invoice-done"
                >
                  <i class="fas fa-check-circle mr-1"></i>Đã xuất HĐ #{{ customerBill.billNumber }}
                  <i class="fas fa-external-link-alt ml-1"></i>
                </a>
                <b-button
                  v-else-if="isSettled"
                  variant="primary"
                  size="sm"
                  :disabled="preview.customer.total <= 0"
                  @click="createBill('CUSTOMER')"
                >
                  <i class="fas fa-plus mr-1"></i>Tạo hoá đơn
                </b-button>
                <span v-else class="text-muted small">Chưa xuất hoá đơn</span>
              </div>
            </div>
            <!-- Bảo hiểm trả (chỉ khi có phần BH) -->
            <div v-if="hasInsurance" class="stl-invoice-row">
              <div class="stl-invoice-payer">
                <i class="fas fa-shield-alt stl-ic-insurance"></i> Bảo hiểm trả
                <span class="stl-invoice-amount">{{ formatPrice(preview.insurance.total) }}</span>
              </div>
              <div class="stl-invoice-act">
                <a
                  v-if="insuranceBill"
                  :href="billLink(insuranceBill)"
                  target="_blank"
                  class="stl-invoice-done"
                >
                  <i class="fas fa-check-circle mr-1"></i>Đã xuất HĐ #{{ insuranceBill.billNumber }}
                  <i class="fas fa-external-link-alt ml-1"></i>
                </a>
                <b-button
                  v-else-if="isSettled"
                  variant="info"
                  size="sm"
                  @click="createBill('INSURANCE')"
                >
                  <i class="fas fa-plus mr-1"></i>Tạo hoá đơn
                </b-button>
                <span v-else class="text-muted small">Chưa xuất hoá đơn</span>
              </div>
            </div>
          </div>
          <p v-if="isSettled" class="stl-invoice-warn text-muted small mb-0">
            <i class="fas fa-exclamation-circle mr-1"></i>
            Đã xuất hoá đơn thì <b>không hoàn tác</b> được quyết toán (tránh lệch kho ↔ sổ). Muốn sửa,
            hãy <b>xoá hoá đơn</b> ở màn Hoá đơn bán lẻ trước.
          </p>
        </div>

        <!-- ============ Phiếu xuất kho dự kiến (read-only) ============ -->
        <div v-if="stockslipPreview.length" class="stl-slip mt-4">
          <div class="stl-slip-head">
            <i class="fas fa-dolly mr-1"></i>
            <b>Phiếu xuất kho dự kiến</b>
            <span class="stl-slip-tag">sẽ sinh khi Duyệt quyết toán</span>
            <div class="text-muted small mt-1">
              Lấy từ <b>vật tư thực dùng</b> (phiếu lĩnh vật tư), đơn giá là <b>giá vốn</b> — khác
              giá bán trên hoá đơn khách. Khối này <b>không đổi</b> khi tick Khách / BH.
            </div>
          </div>
          <div
            v-for="(slip, i) in stockslipPreview"
            :key="i"
            class="stl-slip-card"
          >
            <div class="stl-slip-meta">
              <span>Kho nguồn: <b>{{ slip.sourceWarehouseName }}</b></span>
              <span>Mã dự kiến: <code>{{ slip.expectedCode }}</code></span>
            </div>
            <div class="table-responsive">
              <table class="table table-bordered stl-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 40px">#</th>
                    <th>Vật tư</th>
                    <th class="text-center" style="width: 70px">SL</th>
                    <th class="text-right" style="width: 130px">Đơn giá</th>
                    <th class="text-right" style="width: 140px">Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(ln, j) in slip.lines" :key="j">
                    <td class="text-center">{{ j + 1 }}</td>
                    <td>{{ ln.name }}</td>
                    <td class="text-center">{{ ln.quantity }}</td>
                    <td class="text-right">{{ formatPrice(ln.unitPrice) }}</td>
                    <td class="text-right">{{ formatPrice(ln.totalAmount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import { SO_STATUS, SO_PERMISSION } from '@/core/constants/serviceOrder';
import { convertPrice, makeToastSuccess, makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';

const num = (v) => Number(v) || 0;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default {
  name: 'SettlementPanel',
  props: {
    serviceOrderId: { type: String, required: true },
    soStatus: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      undoing: false,
      hasDraft: false,
      lines: [],
      stockslipPreview: [],
      quantityMismatch: [],
      bills: [],
    };
  },
  computed: {
    canSettle() {
      return checkPermissions([SO_PERMISSION.SETTLE]);
    },
    isSettled() {
      return this.soStatus === SO_STATUS.SETTLED;
    },
    // Đã xuất xưởng → panel CHỈ XEM: không tạo hoá đơn, không hoàn tác, không sửa.
    isReleased() {
      return this.soStatus === SO_STATUS.RELEASED;
    },
    // Có phần Bảo hiểm chi trả → hiện dòng "Bảo hiểm trả".
    hasInsurance() {
      return num(this.preview.insurance.total) > 0;
    },
    // Hoá đơn đã tạo từ phiếu (BE trả trong getSettlement). 1 người trả = tối đa 1 hoá đơn.
    customerBill() {
      return this.bills.find((b) => b.payer === 'CUSTOMER') || null;
    },
    insuranceBill() {
      return this.bills.find((b) => b.payer === 'INSURANCE') || null;
    },
    // Đã có bất kỳ hoá đơn nào → khoá Hoàn tác (BE cũng chặn 409, đây chỉ là UX).
    hasBills() {
      return this.bills.length > 0;
    },
    // Chỉ sửa phân bổ khi phiếu đang quyết toán (SETTLING) + có quyền. SETTLED → read-only.
    editable() {
      return this.soStatus === SO_STATUS.SETTLING && this.canSettle;
    },
    // Id các dòng cha (đã bị tách) — không tính vào tổng.
    parentIds() {
      return new Set(this.lines.filter((l) => l.parentId).map((l) => l.parentId));
    },
    // Sắp xếp hiển thị: dòng thường / dòng cha kèm ngay các dòng con. Mirror BE computePreview.
    rows() {
      const childrenByParent = {};
      this.lines.forEach((l) => {
        if (l.parentId) {
          (childrenByParent[l.parentId] = childrenByParent[l.parentId] || []).push(l);
        }
      });
      const out = [];
      let seq = 0;
      this.lines.forEach((l) => {
        if (l.parentId) return; // dòng con render dưới dòng cha
        seq += 1;
        if (this.parentIds.has(l.id)) {
          out.push({ type: 'parent', line: l, seq });
          (childrenByParent[l.id] || []).forEach((c) =>
            out.push({ type: 'child', line: c, seq }),
          );
        } else {
          out.push({ type: 'normal', line: l, seq });
        }
      });
      return out;
    },
    // 2 hoá đơn theo billTarget (bỏ dòng cha đã tách — chỉ tính dòng lá). Mirror BE computePreview.
    // VAT theo TỪNG dòng (Cách 1), tính NGƯỢC vì amount (từ giá bán) ĐÃ GỒM VAT (FB P9 phần B):
    //   preVat = round2(amount / (1 + vatRate/100)); vat = amount − preVat.
    preview() {
      const customer = { goods: 0, vat: 0, total: 0 };
      const insurance = { goods: 0, vat: 0, total: 0 };
      this.lines.forEach((l) => {
        if (this.parentIds.has(l.id)) return;
        const amount = num(l.amount);
        const preVat = round2(amount / (1 + num(l.vatRate) / 100));
        const vat = round2(amount - preVat);
        const bucket = l.billTarget === 'INSURANCE' ? insurance : customer;
        bucket.goods = round2(bucket.goods + preVat);
        bucket.vat = round2(bucket.vat + vat);
        bucket.total = round2(bucket.goods + bucket.vat);
      });
      return { customer, insurance };
    },
    // Kiểm tra tổng dòng con = dòng cha (mirror BE validateSplit) — chặn Lưu nếu lệch.
    splitError() {
      const byId = new Map(this.lines.map((l) => [l.id, l]));
      const sum = {};
      this.lines.forEach((l) => {
        if (l.parentId) sum[l.parentId] = (sum[l.parentId] || 0) + num(l.amount);
      });
      for (const pid of Object.keys(sum)) {
        const parent = byId.get(pid);
        if (parent && Math.round(sum[pid]) !== Math.round(num(parent.amount))) {
          return `Tách dòng "${parent.desc}": tổng 2 phần (${this.formatPrice(
            sum[pid],
          )}) phải bằng dòng gốc (${this.formatPrice(parent.amount)}).`;
        }
      }
      return '';
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatPrice(v) {
      return convertPrice(num(v)) + ' đ';
    },
    sourceLabel(s) {
      return s === 'MATERIAL' ? 'Vật tư' : 'Công/DV';
    },
    targetLabel(t) {
      return t === 'INSURANCE' ? 'BH trả' : 'Khách trả';
    },
    vatLabel(rate) {
      return `${num(rate)}%`;
    },
    async load() {
      if (
        ![
          SO_STATUS.SETTLING,
          SO_STATUS.SETTLED,
          SO_STATUS.RELEASED,
        ].includes(this.soStatus)
      ) {
        this.hasDraft = false;
        return;
      }
      this.loading = true;
      try {
        const res = await ServiceOrderService.getSettlement(this.serviceOrderId);
        const draft = get(res, 'data.data', null);
        this.lines = (draft && Array.isArray(draft.lines) ? draft.lines : []).map((l) =>
          this.cloneLine(l),
        );
        // Preview phiếu xuất kho dự kiến (read-only) — BE trả kèm, tĩnh (không đổi khi tick).
        this.stockslipPreview = get(draft, 'stockslipPreview', []) || [];
        // Vật tư lệch SL báo giá / thực dùng — chỉ cảnh báo, không chặn quyết toán.
        this.quantityMismatch = get(draft, 'quantityMismatch', []) || [];
        // Hoá đơn đã tạo từ phiếu — để chặn xuất trùng + link "đã xuất HĐ".
        this.bills = get(draft, 'bills', []) || [];
        this.hasDraft = !!draft;
      } catch (e) {
        this.hasDraft = false;
        this.quantityMismatch = [];
        makeToastFaile(this.errMsg(e, 'Lỗi tải nháp quyết toán'));
      } finally {
        this.loading = false;
      }
    },
    cloneLine(l) {
      return {
        id: l.id || uuidv4(),
        source: l.source === 'MATERIAL' ? 'MATERIAL' : 'LABOR',
        refId: l.refId || null,
        desc: l.desc || '',
        qty: num(l.qty),
        unitPrice: num(l.unitPrice),
        amount: num(l.amount),
        vatRate: num(l.vatRate),
        billTarget: l.billTarget === 'INSURANCE' ? 'INSURANCE' : 'CUSTOMER',
        parentId: l.parentId || null,
      };
    },
    // Tick người trả cho 1 dòng thường.
    setTarget(line, val) {
      line.billTarget = val === 'INSURANCE' ? 'INSURANCE' : 'CUSTOMER';
    },
    // Tách 1 dòng → 2 dòng con (Khách + BH), chia đôi thành tiền (tổng con = cha).
    splitLine(line) {
      const half = Math.round(num(line.amount) / 2);
      const childKhach = {
        ...this.cloneLine(line),
        id: uuidv4(),
        parentId: line.id,
        billTarget: 'CUSTOMER',
        desc: `${line.desc} (Khách trả)`,
        amount: half,
      };
      const childBH = {
        ...this.cloneLine(line),
        id: uuidv4(),
        parentId: line.id,
        billTarget: 'INSURANCE',
        desc: `${line.desc} (BH trả)`,
        amount: num(line.amount) - half,
      };
      this.lines.push(childKhach, childBH);
    },
    // Sửa thành tiền 1 dòng con → tự cân dòng con còn lại để tổng = dòng cha.
    onChildAmount(child) {
      const parent = this.lines.find((l) => l.id === child.parentId);
      if (!parent) return;
      const sibling = this.lines.find(
        (l) => l.parentId === child.parentId && l.id !== child.id,
      );
      let v = num(child.amount);
      if (v < 0) v = 0;
      if (v > num(parent.amount)) v = num(parent.amount);
      child.amount = v;
      if (sibling) sibling.amount = num(parent.amount) - v;
    },
    // Gộp lại: bỏ 2 dòng con của 1 dòng cha (dòng cha lại tính vào tổng).
    unsplit(parent) {
      this.lines = this.lines.filter((l) => l.parentId !== parent.id);
    },
    async save() {
      if (this.splitError) return;
      this.saving = true;
      try {
        await ServiceOrderService.updateSettlementLines(this.serviceOrderId, this.lines);
        makeToastSuccess('Lưu phân bổ quyết toán thành công');
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Lưu phân bổ thất bại'));
      } finally {
        this.saving = false;
      }
    },
    // Link mở hoá đơn đã tạo (màn cập nhật HĐBL nhận id = billNumber).
    billLink(bill) {
      return `#/bills/update-bill?id=${bill.billNumber}`;
    },
    // Sang trang add-bill đổ sẵn dòng của 1 người trả (Khách/BH) theo GIÁ BÁN (fromServiceOrderId).
    createBill(payer) {
      this.$router.push({
        name: 'add-bill',
        query: { fromServiceOrderId: this.serviceOrderId, payer },
      });
    },
    // Hoàn tác quyết toán (có xác nhận). SETTLED → trả tồn + xoá XKDV; SETTLING → xoá nháp.
    async undo() {
      const msg =
        this.soStatus === SO_STATUS.SETTLED
          ? 'Hoàn tác sẽ TRẢ LẠI tồn vật tư và XOÁ phiếu xuất kho dịch vụ đã sinh. Tiếp tục?'
          : 'Hoàn tác sẽ xoá nháp phân bổ và đưa phiếu về trạng thái nghiệm thu. Tiếp tục?';
      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: 'Xác nhận hoàn tác quyết toán',
        okTitle: 'Hoàn tác',
        cancelTitle: 'Huỷ',
        okVariant: 'danger',
        centered: true,
      });
      if (!ok) return;
      this.undoing = true;
      try {
        await ServiceOrderService.undoSettlement(this.serviceOrderId);
        makeToastSuccess('Đã hoàn tác quyết toán');
        this.$emit('changed');
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Hoàn tác thất bại'));
      } finally {
        this.undoing = false;
      }
    },
    errMsg(e, fallback) {
      return get(e, 'response.data.message') || fallback;
    },
  },
};
</script>

<style scoped>
/* ---- Cảnh báo lệch SL báo giá / thực dùng ---- */
.stl-mismatch {
  border-left: 4px solid #ffa800;
  font-size: 0.88rem;
}
.stl-mismatch-list {
  padding-left: 18px;
  margin-top: 4px;
}
.stl-mismatch-note {
  font-size: 0.82rem;
  color: #7e8299;
}
/* ---- Preview 2 bill ---- */
.stl-total-card {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 14px 16px;
}
.stl-total-customer {
  background: #e8fff3;
  border-color: #c9f7f5;
}
.stl-total-insurance {
  background: #fff8ec;
  border-color: #ffe2b8;
}
.stl-total-label {
  font-size: 0.9rem;
  color: #3f4254;
  font-weight: 600;
  margin-bottom: 6px;
}
.stl-total-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #7e8299;
  padding: 2px 0;
}
.stl-total-sum {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 4px;
  padding-top: 6px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #3f4254;
}
.stl-total-tag {
  font-size: 0.7rem;
  color: #b5b5c3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 6px;
  text-align: right;
}
/* ---- Bảng dòng ---- */
.stl-table th {
  background: #f3f6f9;
  font-size: 12px;
  vertical-align: middle;
}
.stl-table td {
  font-size: 13px;
}
.stl-row-parent > td {
  background: #f9f9fb;
  color: #b5b5c3;
}
.stl-row-child > td {
  background: #fcfcfd;
}
/* ---- Ghi chú + thanh hành động ---- */
.stl-hint {
  background: #f8f9fc;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
}
.stl-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 0 4px;
  border-top: 1px solid #ebedf3;
}
.stl-actions-btns {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.stl-state-pill {
  font-size: 0.85rem;
  padding: 7px 12px;
  font-weight: 600;
}
.stl-state-pill--draft {
  color: #7e8299;
  border: 1px solid #ebedf3;
  background: #f8f9fc;
}
/* ---- Khối xuất hoá đơn ---- */
.stl-invoice {
  margin-top: 14px;
  border: 1px solid #ebedf3;
  border-radius: 8px;
  overflow: hidden;
}
.stl-invoice-head {
  background: #f3f6f9;
  padding: 10px 14px;
  font-size: 0.95rem;
  color: #3f4254;
}
.stl-invoice-note {
  margin-left: 8px;
  font-size: 0.75rem;
  color: #b5b5c3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.stl-invoice-body {
  padding: 6px 14px;
}
.stl-invoice-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 0;
}
.stl-invoice-row + .stl-invoice-row {
  border-top: 1px dashed #ebedf3;
}
.stl-invoice-payer {
  font-size: 0.92rem;
  font-weight: 600;
  color: #3f4254;
}
.stl-ic-customer {
  color: #1bc5bd;
  margin-right: 4px;
}
.stl-ic-insurance {
  color: #ffa800;
  margin-right: 4px;
}
.stl-invoice-amount {
  margin-left: 10px;
  font-weight: 700;
  color: #3f4254;
}
.stl-invoice-done {
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1bc5bd;
  background: #e8fff3;
  border: 1px solid #c9f7f5;
  border-radius: 6px;
  padding: 6px 12px;
}
.stl-invoice-done:hover {
  text-decoration: none;
  background: #d7fbef;
}
.stl-invoice-warn {
  padding: 8px 14px 12px;
  border-top: 1px solid #ebedf3;
}
/* ---- Phiếu xuất kho dự kiến ---- */
.stl-slip-head {
  font-size: 0.95rem;
  color: #3f4254;
  margin-bottom: 8px;
}
.stl-slip-tag {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.72rem;
  color: #b87503;
  background: #fff4de;
  border: 1px solid #ffe2b8;
  border-radius: 4px;
  padding: 1px 6px;
  vertical-align: middle;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.stl-slip-card {
  border: 1px dashed #d2d4d6;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fcfcfd;
}
.stl-slip-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 8px;
}
.stl-slip-meta code {
  color: #f64e60;
}
</style>
